import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Printer, ArrowLeft, FilePdf } from "@phosphor-icons/react";

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-6 text-sm border-b border-gray-200 py-2">
    <span className="text-gray-500">{k}</span><span className="font-semibold text-right">{v || "—"}</span>
  </div>
);

export default function AdminAgreementView() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/agreements/${id}/full`).then(({ data }) => setA(data)).catch(() => setErr("Agreement not found."));
  }, [id]);

  if (err) return <div className="font-mono-tech text-cyber-red" data-testid="contract-error">/ {err}</div>;
  if (!a) return <div className="font-mono-tech text-white/40">/ loading contract...</div>;

  const signed = !!a.signed_at;
  const money = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 })} USD`;

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/admin/agreements/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `FintechOmni-Agreement-${a.reference}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setErr("Could not generate PDF. Please try again.");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div data-testid="admin-contract-view">
      <div className="flex items-center justify-between mb-6 print:hidden">
        <Link to="/admin/agreements" className="font-mono-tech text-xs text-white/60 hover:text-white flex items-center gap-2" data-testid="contract-back-link">
          <ArrowLeft size={14} /> Back to contracts
        </Link>
        <div className="flex items-center gap-3">
          <button onClick={() => window.print()} className="font-mono-tech text-xs text-white/60 hover:text-white flex items-center gap-2" data-testid="contract-print-btn">
            <Printer size={16} weight="bold" /> Print
          </button>
          <button onClick={downloadPdf} disabled={pdfBusy} className="btn-neon disabled:opacity-50" data-testid="contract-download-pdf-btn">
            <FilePdf size={16} weight="bold" /> {pdfBusy ? "Generating..." : "Download PDF"}
          </button>
        </div>
      </div>

      <div className="bg-white text-gray-900 max-w-3xl mx-auto p-8 sm:p-12 print:p-0 print:max-w-none" data-testid="contract-document">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-gray-400">Sponsorship agreement — v{a.terms_version}</div>
        <h1 className="font-black text-3xl mt-2">FintechOmni.</h1>
        <p className="text-xs text-gray-500 mt-2">{a.event_line}</p>
        <p className="text-xs text-gray-500">Reference: <span className="font-mono font-bold">{a.reference}</span> · Created {new Date(a.created_at).toLocaleDateString()} by {a.created_by}</p>

        <div className="mt-8">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-2">Parties</div>
          <Row k="Organizer" v="Intent Logic LLC (dba FintechOmni)" />
          <Row k="Sponsor (legal entity)" v={a.company_legal_name || a.sponsor_company} />
          <Row k="Sponsor address" v={a.address} />
          <Row k="Sponsor contact" v={`${a.contact_name} · ${a.contact_email}${a.phone ? ` · ${a.phone}` : ""}`} />
        </div>

        <div className="mt-8">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-2">Sponsorship package &amp; pricing</div>
          <Row k="Package" v={a.package_label} />
          <Row k="Event" v={a.event_title} />
          {a.appendix_notes && <Row k="Agreed terms" v={a.appendix_notes} />}
          <Row k="Total fee" v={money(a.amount)} />
        </div>

        <div className="mt-8">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-2">Terms &amp; conditions</div>
          {a.terms_sections.map((s) => (
            <div key={s.h} className="mt-3">
              <div className="font-bold text-sm">{s.h}</div>
              <p className="text-gray-600 text-xs leading-relaxed mt-0.5 whitespace-pre-line">{s.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 border-2 border-gray-900 p-5" data-testid="contract-signature-block">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-2">Electronic signature</div>
          {signed ? (
            <>
              <Row k="Signed by" v={`${a.signed_name} (${a.signed_title})`} />
              <Row k="On behalf of" v={a.company_legal_name} />
              <Row k="Signed at" v={new Date(a.signed_at).toLocaleString()} />
              <Row k="Signer IP" v={a.signer_ip} />
              <Row k="Terms version accepted" v={a.terms_version} />
            </>
          ) : (
            <p className="text-sm text-gray-500">Not yet signed.</p>
          )}
        </div>

        <div className="mt-6 border border-gray-300 p-5" data-testid="contract-payment-block">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-2">Payment record</div>
          <Row k="Status" v={a.status} />
          <Row k="Method" v={a.payment_method} />
          {a.paid_at && <Row k="Paid at" v={new Date(a.paid_at).toLocaleString()} />}
          {a.marked_paid_by && <Row k="Wire confirmed by" v={a.marked_paid_by} />}
          {a.payment_record && (
            <>
              <Row k="Sponsorship fee" v={money(a.payment_record.base_amount ?? a.amount)} />
              {a.payment_record.card_fee ? <Row k="Card processing fee" v={money(a.payment_record.card_fee)} /> : null}
              <Row k="Total charged (card)" v={money(a.payment_record.amount)} />
              <Row k="Stripe session" v={a.payment_record.session_id} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
